import { useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Onboarding } from "../../../lib/apiTyped";
import { useApiCall } from "../../../lib/useToastError";
import { useTour } from "../../tour/useTour";

type Props = {
  mode?: "owner" | "member";
  orgId: string | null;
  displayName: string;
  designPartner: boolean;
  onBack: () => void;
};

const CONFETTI_COLORS = ["#0F0F0F", "#C8A951", "#D8D3F0", "#F5EDE0"];

export function StepDone({
  mode = "owner",
  orgId,
  displayName,
  designPartner,
  onBack,
}: Props) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { run } = useApiCall();
  const tour = useTour();

  const firstName = displayName.split(/\s+/)[0] || displayName;
  const isMember = mode === "member";

  function fireConfetti() {
    const base = { colors: CONFETTI_COLORS, zIndex: 9999, ticks: 220 };
    confetti({
      ...base,
      particleCount: 140,
      spread: 80,
      origin: { y: 0.4 },
      scalar: 1,
    });
    setTimeout(() => {
      confetti({
        ...base,
        particleCount: 60,
        angle: 60,
        spread: 60,
        origin: { x: 0, y: 0.65 },
      });
      confetti({
        ...base,
        particleCount: 60,
        angle: 120,
        spread: 60,
        origin: { x: 1, y: 0.65 },
      });
    }, 220);
  }

  async function finish() {
    if (busy) return;
    if (mode === "owner" && !orgId) return;
    // Fire confetti BEFORE flipping busy / awaiting anything. Two reasons:
    // (1) any later await yields the event loop and on slow machines the
    // canvas mount can race with the navigate that unmounts this view; (2)
    // an immediate visual response confirms the click even when the API is
    // slow.
    fireConfetti();
    setBusy(true);
    const result = await run(() =>
      isMember
        ? Onboarding.completeForUser()
        : Onboarding.complete(orgId as string),
    );
    if (!result) {
      setBusy(false);
      return;
    }
    // Navigate first so the dashboard mounts behind the spotlight, then
    // start the tour. The delay lets the confetti fully fall and the
    // dashboard finish its initial render before the tour measures targets.
    setTimeout(() => {
      navigate("/", { replace: true });
      setTimeout(() => tour.start(), 400);
    }, 1500);
  }

  return (
    <section className="space-y-10 text-center sm:text-left">
      <div className="space-y-4">
        {designPartner && (
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ink"
            style={{
              backgroundColor: "#F5EDE0",
              border: "1px solid rgba(200,169,81,0.35)",
            }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Design partner
          </span>
        )}
        <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-ink leading-[1.05]">
          Todo listo
          {firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-base sm:text-lg text-ink-2 max-w-xl">
          {isMember
            ? "Bienvenido/a a Formular. Te enseñamos en 30 segundos dónde está cada cosa."
            : "Bienvenido/a a Formular. A partir de ahora cada factura, plan y miembro que añadas vivirá aquí."}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 justify-center sm:justify-start">
        <button
          type="button"
          onClick={onBack}
          disabled={busy}
          className="inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink transition-colors disabled:opacity-40"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Atrás
        </button>
        <button
          type="button"
          onClick={finish}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-ink text-white text-lg font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-[1.04] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {busy ? "Lanzando…" : "¡Empezar!"}
        </button>
      </div>
    </section>
  );
}
